import { withAuthenticator } from '@aws-amplify/ui-react'
import { IdleController } from '@components/index'
import * as Sentry from '@sentry/react'
import { Auth } from 'aws-amplify'
import React from 'react'
import { QueryClientProvider } from 'react-query'
import { AvailContextProvider } from 'src/contexts/avail-context'

import { TourProvider } from '@/components/tour/components/tour-provider'
import { Toaster } from '@/components/ui/toaster'
import { AuthContextProvider } from '@/contexts/auth-context'
import { FilesContextProvider } from '@/contexts/files-context'
import { AutocompleteProvider } from '@/contexts/map-autocomplete-context'
import { MapProvider } from '@/contexts/map-context'
import { MarkersProvider } from '@/contexts/markers-context'
import { SidebarContextProvider } from '@/contexts/sidebar-context'
import { FilterProvider } from '@/entities/avail/hooks/FilteringHook'
import { env } from '@/env'
import { queryClient } from '@/lib/react-query'
import { tourConfig } from '@/lib/reactour'
import { RateSelectionProvider } from '@/pages/avail/hooks/rate-selecion-context'
import useScrollToTop from '@/shared/hooks/useScrollToTop'

import { httpClient } from '../adapters/axios/axios'
import { AvailVipProvider } from './availVip'
import { CancelBookingProvider } from './cancelBooking'
import { NotificationProvider } from './notification'
import { RequestCancelEditProvider } from './requestCancelEdit'
import { SidebarProvider } from './sideBar'

interface AppProviderProps {
  children: React.ReactNode
}

interface AppProviderProps {
  children: React.ReactNode
}

function AppProvider({ children }: AppProviderProps): JSX.Element {
  useScrollToTop()
  return (
    <RateSelectionProvider>
      <AvailContextProvider>
        <httpClient.AxiosInterceptor>
          <httpClient.AxiosInterceptor>
            <QueryClientProvider client={queryClient}>
              <IdleController timeout={1000 * 60 * 60 * 8}>
                <TourProvider config={tourConfig}>
                  <NotificationProvider>
                    <AvailVipProvider>
                      <MapProvider>
                        <AutocompleteProvider>
                          <MarkersProvider>
                            <FilterProvider>
                              <FilesContextProvider>
                                <RequestCancelEditProvider>
                                  <CancelBookingProvider>
                                    <SidebarContextProvider>
                                      <AuthContextProvider>
                                        <SidebarProvider>
                                          {children}
                                        </SidebarProvider>
                                      </AuthContextProvider>
                                    </SidebarContextProvider>
                                  </CancelBookingProvider>
                                </RequestCancelEditProvider>
                              </FilesContextProvider>
                            </FilterProvider>
                          </MarkersProvider>
                        </AutocompleteProvider>
                      </MapProvider>
                    </AvailVipProvider>
                  </NotificationProvider>
                </TourProvider>
              </IdleController>
            </QueryClientProvider>
          </httpClient.AxiosInterceptor>
        </httpClient.AxiosInterceptor>
        <Toaster />
      </AvailContextProvider>
    </RateSelectionProvider>
  )
}

let ExportedComponent: React.FC<AppProviderProps>

const isAuthenticated = async (): Promise<boolean> => {
  try {
    const user = await Auth.currentAuthenticatedUser()
    Sentry.setUser({
      id: user.username,
      email: user.attributes.email,
    })

    return true
  } catch {
    return false
  }
}

if (
  ['development', 'homolog', 'internal', 'booking-dev'].includes(
    env.REACT_APP_ENV,
  )
) {
  ExportedComponent = withAuthenticator(AppProvider)
} else {
  const AuthenticatedOrRedirect: React.FC<AppProviderProps> = (props) => {
    React.useEffect(() => {
      const checkAuth = async (): Promise<void> => {
        const isAuth = await isAuthenticated()
        if (!isAuth) {
          window.location.href = `${env.REACT_APP_HUB_ROUTE}/dashboard`
        }
      }

      checkAuth()
    }, [])

    return <AppProvider {...props} />
  }

  ExportedComponent = AuthenticatedOrRedirect
}

export default ExportedComponent
